import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  ArrowDownRight,
  ArrowUpRight,
  ChartBar,
  ChevronRight,
  MapPin,
  PieChart,
  Sparkles,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { KPICard } from '@/components/charts/KPICard';
import { LineChart } from '@/components/charts/LineChart';
import { QuadrantChart } from '@/components/charts/QuadrantChart';
import { ChartModeToggle, type ChartGranularity, type SeasonalityMode } from '@/components/charts/ChartModeToggle';
import { mergeBucketedPrevious, mergeIndexedPrevious } from '@/components/charts/compareSeries';
import {
  bucketBySeasonality,
  disabledGranularitiesFor,
  granularityForSeasonality,
} from '@/components/charts/seasonality';
import { useFilters } from '@/state/filters';
import { SnapshotMenu } from '@/components/system/SnapshotMenu';
import { TaxonomyRail } from '@/components/inventory/TaxonomyRail';
import { HierarchyMatrix } from '@/components/products/HierarchyMatrix';
import { MixDonut } from '@/components/products/MixDonut';
import { ProductLeaderboards } from '@/pages/products/ProductLeaderboards';
import { DimensionToggle } from '@/pages/products/DimensionToggle';
import { MerchandisingInsights } from '@/pages/products/MerchandisingInsights';
import {
  useInventoryTaxonomy,
  useProductPerformance,
  useProductTimeseries,
  useProductQuadrant,
  type ProductGroupBy,
  type ProductPerfRow,
} from '@/api/bi';
import { useProductScope } from '@/pages/products/useProductScope';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

export function ProductsPage() {
  const scope = useProductScope();
  const navigate = useNavigate();
  const taxonomy = useInventoryTaxonomy();

  const range = useFilters((s) => s.range);
  const [dimension, setDimension] = useState<ProductGroupBy>('brand');
  const [granularity, setGranularity] = useState<ChartGranularity>(pickGranularity(scope.iso));
  const [seasonality, setSeasonality] = useState<SeasonalityMode>('none');

  const disabledGranularities = useMemo(
    () => disabledGranularitiesFor(range.from, range.to),
    [range.from, range.to],
  );
  const requiredGranularity = granularityForSeasonality(seasonality);
  const effectiveGranularity: ChartGranularity = requiredGranularity ?? granularity;

  const chartMetrics = ['gmv', 'units', 'orders'] as const;
  const compareSeriesExtras = scope.compareEnabled
    ? [
        { key: 'gmv_prev', label: 'GMV prev', color: 'hsl(var(--primary) / 0.55)', strokeDasharray: '4 4' },
        { key: 'units_prev', label: 'Units prev', color: 'hsl(var(--info) / 0.55)', strokeDasharray: '4 4' },
        { key: 'orders_prev', label: 'Orders prev', color: 'hsl(var(--warning) / 0.55)', strokeDasharray: '4 4' },
      ]
    : [];

  const perf = useProductPerformance({
    start: scope.iso.start,
    end: scope.iso.end,
    compareStart: scope.compareEnabled ? scope.compareRange?.start : undefined,
    compareEnd: scope.compareEnabled ? scope.compareRange?.end : undefined,
    groupBy: dimension,
    limit: 250,
    ...scope.taxonomy,
  });

  const series = useProductTimeseries({
    start: scope.iso.start,
    end: scope.iso.end,
    granularity: effectiveGranularity,
    ...scope.taxonomy,
  });
  const prevSeries = useProductTimeseries({
    start: scope.compareRange?.start ?? '',
    end: scope.compareRange?.end ?? '',
    granularity: effectiveGranularity,
    enabled: scope.compareEnabled && Boolean(scope.compareRange),
    ...scope.taxonomy,
  });

  const quadrant = useProductQuadrant({
    start: scope.iso.start,
    end: scope.iso.end,
    groupBy: dimension,
    limit: 60,
    ...scope.taxonomy,
  });

  const totals = perf.data?.totals;
  const pareto = perf.data?.pareto;

  const chartData = useMemo(
    () =>
      mergeIndexedPrevious(
        series.data?.buckets ?? [],
        prevSeries.data?.buckets,
        [...chartMetrics],
        scope.compareEnabled,
      ),
    [series.data, prevSeries.data, scope.compareEnabled],
  );

  const seasonalityChartData = useMemo(() => {
    if (seasonality === 'none') return [];
    const current = bucketBySeasonality(series.data?.buckets ?? [], {
      timeKey: 't',
      metrics: [...chartMetrics],
      mode: seasonality,
      aggregate: 'sum',
    });
    const previous = bucketBySeasonality(prevSeries.data?.buckets ?? [], {
      timeKey: 't',
      metrics: [...chartMetrics],
      mode: seasonality,
      aggregate: 'sum',
    });
    return mergeBucketedPrevious(current, previous, [...chartMetrics], scope.compareEnabled);
  }, [series.data, prevSeries.data, seasonality, scope.compareEnabled]);

  const gmvTrend = useMemo(
    () => (series.data?.buckets ?? []).map((b) => ({ value: b.gmv })),
    [series.data],
  );
  const unitsTrend = useMemo(
    () => (series.data?.buckets ?? []).map((b) => ({ value: b.units })),
    [series.data],
  );
  const ordersTrend = useMemo(
    () => (series.data?.buckets ?? []).map((b) => ({ value: b.orders })),
    [series.data],
  );

  const movers = useMemo(() => deriveMovers(perf.data?.rows ?? []), [perf.data]);

  const handleDimensionRow = (row: ProductPerfRow) => {
    if (dimension === 'product') navigate(`/products/${row.id}`);
    else applyDimensionFilter(scope, dimension, row.id);
  };

  // Carry the active taxonomy scope into the ward map as query params so
  // "map what I'm looking at" is one click.
  const mapHref = useMemo(() => {
    const params = new URLSearchParams();
    const add = (key: string, ids: number[]) => {
      if (ids.length) params.set(key, ids.join(','));
    };
    add('vendor_id', scope.vendorIds);
    add('brand_id', scope.brandIds);
    add('category_group_id', scope.categoryGroupIds);
    add('category_id', scope.categoryIds);
    add('subcategory_id', scope.subcategoryIds);
    if (scope.productQuery.trim()) params.set('pq', scope.productQuery.trim());
    const qs = params.toString();
    return qs ? `/products/map?${qs}` : '/products/map';
  }, [
    scope.vendorIds,
    scope.brandIds,
    scope.categoryGroupIds,
    scope.categoryIds,
    scope.subcategoryIds,
    scope.productQuery,
  ]);

  return (
    <>
      <PageHeader
        eyebrow="Merchandising"
        title="Product analytics"
        description="GMV, units, margin and demand by brand, category, sub-category and SKU — scoped to your period, store and taxonomy filters."
        actions={
          <div className="flex items-center gap-2">
            <Badge tone="info" className="gap-1">
              <Sparkles className="h-3 w-3" /> New
            </Badge>
            <button
              type="button"
              onClick={() => navigate(mapHref)}
              className="press inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-muted-foreground hover:bg-muted hover:text-foreground focus-ring"
            >
              <MapPin className="h-3.5 w-3.5" aria-hidden /> View on map
            </button>
            <SnapshotMenu filename="products" />
          </div>
        }
      />

      <TaxonomyRail
        taxonomy={taxonomy.data}
        taxonomyLoading={taxonomy.isLoading}
        vendorIds={scope.vendorIds}
        onVendorIds={scope.setVendorIds}
        brandIds={scope.brandIds}
        onBrandIds={scope.setBrandIds}
        categoryGroupIds={scope.categoryGroupIds}
        onCategoryGroupIds={scope.setCategoryGroupIds}
        categoryIds={scope.categoryIds}
        onCategoryIds={scope.setCategoryIds}
        subcategoryIds={scope.subcategoryIds}
        onSubcategoryIds={scope.setSubcategoryIds}
        q={scope.productQuery}
        onQ={scope.setProductQuery}
        onClear={scope.hasSelection ? scope.clear : undefined}
      />

      {movers.length > 0 ? <ChangedChips chips={movers} pareto={pareto} /> : null}

      {perf.isError ? (
        <ErrorState onRetry={() => void perf.refetch()} />
      ) : (
        <div className="grid gap-4 stagger sm:grid-cols-2 lg:grid-cols-5">
          <KPICard
            label="Product GMV"
            value={fmt.npr(totals?.gmv ?? 0)}
            trend={gmvTrend}
            loading={perf.isLoading}
            hint="Sum of item price × qty for completed orders in range."
          />
          <KPICard
            label="Units sold"
            value={fmt.compact(totals?.units ?? 0)}
            trend={unitsTrend}
            loading={perf.isLoading}
            hint="Total item quantity across completed orders."
          />
          <KPICard
            label="Orders"
            value={fmt.compact(totals?.orders ?? 0)}
            trend={ordersTrend}
            loading={perf.isLoading}
            hint="Distinct completed orders containing scoped products."
          />
          <KPICard
            label="Gross margin"
            value={totals?.margin_pct != null ? fmt.pct(totals.margin_pct) : '—'}
            loading={perf.isLoading}
            hint="(price − unit cost) ÷ GMV. Legacy items without a cost are excluded from profit."
          />
          <KPICard
            label="Basket AOV"
            value={fmt.npr(totals?.aov ?? 0)}
            loading={perf.isLoading}
            hint="Product GMV ÷ distinct completed orders in range."
          />
        </div>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>GMV, units & orders</CardTitle>
            <ChartModeToggle
              granularity={granularity}
              onGranularityChange={setGranularity}
              disabledGranularities={disabledGranularities}
              seasonality={seasonality}
              onSeasonalityChange={setSeasonality}
            />
          </CardHeader>
          <CardBody>
            {series.isLoading || (scope.compareEnabled && prevSeries.isLoading) ? (
              <Skeleton className="h-[280px] w-full" />
            ) : series.isError ? (
              <ErrorState onRetry={() => void series.refetch()} />
            ) : chartData.length === 0 ? (
              <EmptyState
                icon={<ChartBar className="h-5 w-5" />}
                title="No sales in this period"
                description="Nothing matched the current date range, store and dimension filters."
                className="h-[280px]"
              />
            ) : seasonality !== 'none' ? (
              <LineChart
                data={seasonalityChartData}
                xKey="bucket"
                series={[
                  { key: 'gmv', label: 'GMV (NPR)' },
                  { key: 'units', label: 'Units' },
                  { key: 'orders', label: 'Orders' },
                  ...compareSeriesExtras,
                ]}
                formatX={(s) => s}
                formatValue={(v) => fmt.compact(v)}
              />
            ) : (
              <LineChart
                data={chartData}
                xKey="t"
                series={[
                  { key: 'gmv', label: 'GMV (NPR)' },
                  { key: 'units', label: 'Units' },
                  { key: 'orders', label: 'Orders' },
                  ...compareSeriesExtras,
                ]}
                formatX={(s) => fmt.date(s, effectiveGranularity === 'hour' ? 'HH:mm' : 'd MMM')}
                formatValue={(v) => fmt.compact(v)}
              />
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Revenue mix</CardTitle>
            <DimensionToggle value={dimension} onChange={setDimension} />
          </CardHeader>
          <CardBody>
            {perf.isLoading ? (
              <Skeleton className="h-[220px] w-full" />
            ) : perf.isError ? (
              <ErrorState onRetry={() => void perf.refetch()} />
            ) : (perf.data?.rows.length ?? 0) === 0 ? (
              <EmptyState
                icon={<PieChart className="h-5 w-5" />}
                title="Nothing to break down"
                description="No revenue was recorded for this dimension in the selected period."
                className="h-[220px]"
              />
            ) : (
              <MixDonut
                rows={perf.data?.rows ?? []}
                onSliceClick={(id) => applyDimensionFilter(scope, dimension, id)}
              />
            )}
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        <Card>
          <CardHeader>
            <CardTitle>Hierarchy drill-down</CardTitle>
            <Badge tone="neutral" className="gap-1">
              <ChartBar className="h-3 w-3" /> Group ▸ Category ▸ Sub ▸ SKU
            </Badge>
          </CardHeader>
          <CardBody>
            <HierarchyMatrix
              scope={scope}
              taxonomy={taxonomy.data}
              onProductClick={(row) => navigate(`/products/${row.id}`)}
            />
          </CardBody>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Profitability triage</CardTitle>
            <DimensionToggle value={dimension} onChange={setDimension} />
          </CardHeader>
          <CardBody>
            {quadrant.isLoading ? (
              <Skeleton className="h-[320px] w-full" />
            ) : quadrant.isError ? (
              <ErrorState onRetry={() => void quadrant.refetch()} />
            ) : (
              <QuadrantChart
                points={quadrant.data?.points ?? []}
                formatNpr={(n) => fmt.npr(n)}
                xLabel="GMV share"
                yLabel="Margin Δ vs median"
                zLabel="Gross profit"
                onPointClick={(p) => {
                  const id = typeof p.id === 'number' ? p.id : Number(p.id);
                  if (!Number.isFinite(id)) return;
                  if (dimension === 'product') navigate(`/products/${id}`);
                  else applyDimensionFilter(scope, dimension, id);
                }}
              />
            )}
          </CardBody>
        </Card>

        <ProductLeaderboards
          rows={perf.data?.rows ?? []}
          loading={perf.isLoading}
          dimension={dimension}
          onRowClick={handleDimensionRow}
        />
      </div>

      <MerchandisingInsights scope={scope} />

      <div className="mt-6 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <ChartBar className="h-3.5 w-3.5" aria-hidden />
        <span>
          {pareto ? `${pareto.count} of ${pareto.total} ${dimension.replace('_', ' ')}s drive ~80% of GMV.` : null}
        </span>
        <span className="ml-auto">Completed orders only · Asia/Kathmandu.</span>
      </div>
    </>
  );
}

function pickGranularity(iso: { start: string; end: string }): ChartGranularity {
  const days = Math.max(1, Math.round((new Date(iso.end).getTime() - new Date(iso.start).getTime()) / 86_400_000));
  if (days <= 2) return 'hour';
  if (days <= 60) return 'day';
  return 'week';
}

function applyDimensionFilter(
  scope: ReturnType<typeof useProductScope>,
  dimension: ProductGroupBy,
  id: number | null,
) {
  if (id == null) return;
  if (dimension === 'brand') scope.setBrandIds([id]);
  else if (dimension === 'category_group') scope.setCategoryGroupIds([id]);
  else if (dimension === 'category') scope.setCategoryIds([id]);
  else if (dimension === 'subcategory') scope.setSubcategoryIds([id]);
}

interface Mover {
  label: string;
  pct: number;
}

function deriveMovers(rows: ProductPerfRow[]): Mover[] {
  return rows
    .filter((r) => r.gmv_delta_pct != null && Math.abs(r.gmv_delta_pct) >= 0.05)
    .map((r) => ({ label: r.name, pct: r.gmv_delta_pct as number }))
    .sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct))
    .slice(0, 5);
}

function ChangedChips({
  chips,
  pareto,
}: {
  chips: Mover[];
  pareto?: { count: number; total: number };
}) {
  return (
    <div className="my-4 flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
        <Activity className="h-3 w-3" aria-hidden /> Biggest movers
      </span>
      {chips.map((c) => {
        const positive = c.pct > 0;
        const Icon = positive ? ArrowUpRight : ArrowDownRight;
        return (
          <span
            key={c.label}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset',
              positive
                ? 'border-success/30 bg-success/10 text-success ring-success/20'
                : 'border-danger/30 bg-danger/10 text-danger ring-danger/20',
            )}
          >
            <Icon className="h-3 w-3" aria-hidden />
            <span className="max-w-[12rem] truncate">{c.label}</span>
            <span className="kpi-num">{fmt.delta(c.pct, { unit: 'pct' })}</span>
          </span>
        );
      })}
      {pareto && pareto.total > 1 ? (
        <span className="inline-flex items-center gap-1.5 rounded-full border border-info/30 bg-info/10 px-2 py-0.5 text-[11px] font-medium text-info ring-1 ring-inset ring-info/20">
          <ChevronRight className="h-3 w-3" aria-hidden />
          {pareto.count}/{pareto.total} ≈ 80%
        </span>
      ) : null}
    </div>
  );
}
